import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, DollarSign, Users, Megaphone, LineChart, ArrowRight, Lightbulb, Rocket, Mail, Gift, Star, MessageSquare, Image } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AIGrowthEnginePage = ({ session, profile }: { session: any; profile: any }) => {
  const navigate = useNavigate();

  const toolCards = [
  // Volunteer Engagement tools
  {
    title: "Volunteer Recruitment Emails",
    description: "AI-generated outreach emails to attract and engage potential volunteers",
    icon: <Mail className="h-5 w-5 text-blue-500" />,
    path: "/dashboard/ai-engine/volunteer-engagement/recruitment-emails"
  },
  {
    title: "Volunteer Thank You Emails",
    description: "Generate heartfelt post-event appreciation messages for your volunteers",
    icon: <Gift className="h-5 w-5 text-pink-500" />,
    path: "/dashboard/ai-engine/volunteer-engagement/thank-you-emails"
  },
  {
    title: "Event Promotion Images",
    description: "Generate eye-catching banners and flyers for volunteer events",
    icon: <Image className="h-5 w-5 text-orange-500" />,
    path: "/dashboard/ai-engine/volunteer-engagement/promotion-images"
  },
  {
    title: "Volunteer Spotlight Templates",
    description: "Create engaging social media posts to recognize volunteers publicly",
    icon: <Star className="h-5 w-5 text-yellow-500" />,
    path: "/dashboard/ai-engine/volunteer-engagement/volunteer-spotlights"
  },
  {
    title: "Volunteer Certificate Generator",
    description: "Create and personalize certificates for your volunteers with AI.",
    icon: <Users className="h-5 w-5 text-green-500" />, 
    path: null, // Disable navigation
    comingSoon: true
  },
  // Marketing & Outreach tools
  {
    title: "Social Media Post Generator",
    description: "AI-powered templates for different campaign types and platforms",
    icon: <MessageSquare className="h-5 w-5 text-pink-500" />,
    path: "/dashboard/ai-engine/marketing-outreach/social-posts"
  }
];

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">AI Growth Engine</h1>
        <p className="text-muted-foreground">
          Supercharge your organization with AI-powered tools and insights
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {toolCards.map((tool, idx) => (
          <Card key={tool.title + idx} className="group relative overflow-hidden border border-border/50 bg-background/50 backdrop-blur-sm transition-all hover:border-blue-200 hover:shadow-lg">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="rounded-full bg-blue-50 p-2 transition-colors group-hover:bg-blue-100">
                  {tool.icon}
                </div>
                {tool.comingSoon && (
                  <span className="ml-2 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">Coming Soon</span>
                )}
              </div>
              <CardTitle className="text-xl">{tool.title}</CardTitle>
              <CardDescription>{tool.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white transition-all hover:from-blue-600 hover:to-purple-600 disabled:opacity-60"
                onClick={() => tool.path && navigate(tool.path)}
                disabled={tool.comingSoon}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {tool.comingSoon ? 'Coming Soon' : 'Get Started'}
              </Button>
            </CardContent>
            {/* Decorative corner gradients */}
            <div className="absolute -right-6 -top-6 h-12 w-12 rounded-full bg-gradient-to-br from-blue-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
            <div className="absolute -bottom-6 -left-6 h-12 w-12 rounded-full bg-gradient-to-tr from-purple-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AIGrowthEnginePage;
